import Button from "../../ui/Button";
import { useEffect, useState } from "react";
import {
  AppointmentFormProps,
  EmployeeAvailabilityHours,
} from "../../../types/product";
import { useGetEmployeesThatProvideThisServiceQuery } from "../../../store/api/productApi";
import Spinner from "@/components/loader/Spinner";
import { toast } from "react-hot-toast";
import axios from "axios";
import { Calendar } from "./Calendar";
import { useMerchantSlug } from "@/utils/hooks/useMerchantSlug";
import { useNavigate } from "react-router-dom";

export const AppointmentForm: React.FC<AppointmentFormProps> = ({
  closeAppointmentForm,
  product,
  appointmentDuration,
  handleAddToCart,
  selectedAppointmentDateTime,
  setSelectedAppointmentDateTime,
  selectedServiceProvider,
  setSelectedServiceProvider,
}) => {
  const navigate = useNavigate();
  const merchantSlug = useMerchantSlug();

  const { data: serviceProviders, isLoading } =
    useGetEmployeesThatProvideThisServiceQuery({
      serviceId: product.id,
    });

  const [employeeAvailabilityHours, setEmployeeAvailabilityHours] =
    useState<EmployeeAvailabilityHours>();

  useEffect(() => {
    if (selectedServiceProvider) {
      fetchEmployeeAvailabilityHours(selectedServiceProvider.id);
    }
  }, [selectedServiceProvider]);

  const fetchEmployeeAvailabilityHours = async (employeeId: number) => {
    const apiUrl = `${import.meta.env.VITE_API_BASE_URL}api/v1/employee-availability?employee_id=${employeeId}`;
    try {
      const response = await axios.get(apiUrl);
      setEmployeeAvailabilityHours(response.data);
    } catch (error) {
      console.error("Error fetching employee availability hours:", error);
    }
  };

  const handleProviderSelection = (value: string) => {
    if (value === "") {
      setSelectedServiceProvider(null);
      setEmployeeAvailabilityHours(undefined);
    } else if (
      value === "random" &&
      serviceProviders &&
      serviceProviders.length > 0
    ) {
      const randomIndex = Math.floor(Math.random() * serviceProviders.length);
      const employee = serviceProviders[randomIndex];
      setSelectedServiceProvider(employee);
      toast.success(
        `"${employee.name}" has been selected as the service provider`,
      );
    } else {
      const selected = serviceProviders?.find(
        (provider) => provider.id === Number(value),
      );
      setSelectedServiceProvider(selected || null);
    }
  };

  const navigateToCart = () => {
    const currentPath = window.location.pathname;
    if (currentPath !== `${merchantSlug}/cart`) {
      navigate(`${merchantSlug}/cart`);
    }
  };

  const directCheckout = () => {
    closeAppointmentForm();
    handleAddToCart();
    navigateToCart();
  };

  return (
    <div className="flex flex-col h-full">
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <div className="flex-grow flex flex-col items-center gap-1 overflow-y-scroll p-1 pb-6">
            <div className="w-full max-w-md mb-2">
              <label
                htmlFor="serviceProvider"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Select Service Provider
              </label>
              <select
                id="serviceProvider"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                value={
                  selectedServiceProvider
                    ? selectedServiceProvider.id.toString()
                    : ""
                }
                onChange={(e) => handleProviderSelection(e.target.value)}
                required
              >
                <option value="">Select a service provider</option>
                {serviceProviders?.map((provider) => (
                  <option key={provider.id} value={provider.id.toString()}>
                    {provider.name}
                  </option>
                ))}
                <option value="random">Any Provider</option>
              </select>
            </div>
            <Calendar
              employeeAvailabilityHours={employeeAvailabilityHours || null}
              selectedServiceProvider={selectedServiceProvider}
              appointmentDuration={appointmentDuration}
              setSelectedDateTime={setSelectedAppointmentDateTime}
            />
          </div>
          <div className="md:sticky md:bg-white md:bottom-0 md:pt-1">
            <div className="w-full flex gap-4 justify-center pb-12 md:pb-0">
              <Button
                text="Back"
                variant="secondary"
                onClick={closeAppointmentForm}
                className="w-[90%] h-[3rem] md:h-[3.4rem] rounded-[6px] md:rounded-[8px] text-[16px] md:text-[18px] font-bold flex items-center justify-center"
              />
              <Button
                text="Book Appointment"
                variant="primary"
                disabled={!selectedAppointmentDateTime}
                className="w-[90%] h-[3rem] md:h-[3.4rem] rounded-[6px] md:rounded-[8px] text-[16px] md:text-[18px] font-bold flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={directCheckout}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
